import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button, Divider, Dropdown, Menu, message, TableColumnsType, Tabs, Tooltip } from 'antd';
import ScrollTable from 'components/ScrollTable';
import StatusButton from 'components/StatusButton';
import dayjs from 'dayjs';
import { ReactNode, useState } from 'react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import {
  BuyerFilterParams,
  BuyerPageParams,
  BuyerSortParams,
  getHighRiskBuyers,
  getMaliciousBuyers,
  markHighRiskMalicious,
} from './service';
import { useRequest } from 'ahooks';
import { searchType } from 'services/api/conversation.convList';
import useModal from 'hooks/useModal';
import ConversationsModal, {
  ConversationRecord,
  HasConversationRecord,
  NoConversationRecord,
} from './ConversationsModal';
import { formatNumber } from 'utils/numberFormat';
import BuyerFilter from './BuyerFilter';
import ExportBuyerModal from './ExportBuyerModal';
import ExportTradeModal from './ExportTradeModal';
import ImportMaliciousBuyerModal from './ImportMaliciousBuyerModal';
import { DownloadOutlined, DownOutlined, UploadOutlined } from '@ant-design/icons';
import { What } from 'views/Aide/Scenario/PolicyTable';

type RT = ReviewAnalysis.Buyer;

function Buyer({
  visible,
  onLastTimeChange,
}: {
  visible: boolean;
  onLastTimeChange: (lastTime: string) => void;
}) {
  const { t } = useTranslate();

  const [activeTabKey, setActiveTabKey] = useState<'highRisk' | 'malicious'>('highRisk');

  const defaultPage = {
    pageNum: 1,
    pageSize: 10,
    total: 0,
  };
  const [highRiskPage, setHighRiskPage] = useState(defaultPage);
  const [maliciousPage, setMaliciousPage] = useState(defaultPage);

  const defaultSort: BuyerSortParams = {
    orderBy: 'create_time',
    asc: false,
  };
  const [highRiskSort, setHighRiskSort] = useState(defaultSort);
  const [maliciousSort, setMaliciousSort] = useState(defaultSort);

  const defaultFilter: BuyerFilterParams = {
    filterBuyerNick: '',
  };
  const [highRiskFilter, setHighRiskFilter] = useState(defaultFilter);
  const [maliciousFilter, setMaliciousFilter] = useState(defaultFilter);

  const highRiskState = useRequest(
    ({
      page = highRiskPage,
      sort = highRiskSort,
      filter = highRiskFilter,
    }: {
      page?: BuyerPageParams;
      sort?: BuyerSortParams;
      filter?: BuyerFilterParams;
    } = {}) => {
      return getHighRiskBuyers({
        pageNum: page.pageNum,
        pageSize: page.pageSize,
        ...sort,
        ...filter,
      }).then(({ buyers, total }) => {
        setHighRiskPage({
          ...page,
          total,
        });
        setHighRiskSort(sort);
        setHighRiskFilter(filter);
        if (page.pageNum === 1 && buyers.length) {
          onLastTimeChange(buyers[0].createTime);
        }
        return buyers;
      });
    },
    {
      ready: visible,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );
  const maliciousState = useRequest(
    ({
      page = maliciousPage,
      sort = maliciousSort,
      filter = maliciousFilter,
    }: {
      page?: BuyerPageParams;
      sort?: BuyerSortParams;
      filter?: BuyerFilterParams;
    } = {}) => {
      return getMaliciousBuyers({
        pageNum: page.pageNum,
        pageSize: page.pageSize,
        ...sort,
        ...filter,
      }).then(({ buyers, total }) => {
        setMaliciousPage({
          ...page,
          total,
        });
        setMaliciousSort(sort);
        setMaliciousFilter(filter);
        return buyers;
      });
    },
    {
      ready: visible,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const conversationsModalState = useModal<{
    buyer: RT;
    records: ConversationRecord[];
    actions?: ReactNode[];
  }>();
  const conversationsModal = conversationsModalState.meta && (
    <ConversationsModal
      key={conversationsModalState.meta.buyer.tradeBuyerId}
      visible={conversationsModalState.visible}
      {...conversationsModalState.meta}
      onCancel={() => conversationsModalState.hide()}
      afterClose={() => conversationsModalState.update(false, undefined)}
    />
  );

  const numRender = (num: number) => (num > 3 ? <Red>{num}</Red> : num);
  const ratioRender = (ratio: number) =>
    ratio >= 0.35 ? <Red>{formatNumber(ratio, '0.0%')}</Red> : formatNumber(ratio, '0.0%');

  const page = activeTabKey === 'highRisk' ? highRiskPage : maliciousPage;
  const sort = activeTabKey === 'highRisk' ? highRiskSort : maliciousSort;
  const state = activeTabKey === 'highRisk' ? highRiskState : maliciousState;

  const columns: TableColumnsType<RT> = [
    {
      key: 'buyerNick',
      dataIndex: 'buyerNick',
      title: t('买家昵称'),
      width: 150,
      fixed: 'left',
    },
    {
      key: 'platformRegion',
      title: t('平台站点'),
      width: 160,
      render: (_, { tunnelType, region }) =>
        `${platformLabelMap[toLowerCase(tunnelType)]}-${t(`region.${toLowerCase(region)}`)}`,
    },
    {
      key: 'cumuTrade',
      dataIndex: 'cumuTrade',
      title: t('订单数'),
      width: 120,
    },
    {
      key: 'cumuReturnTrade',
      dataIndex: 'cumuReturnTrade',
      title: t('退货/退款数'),
      width: 140,
      render: numRender,
      sorter: true,
      showSorterTooltip: false,
      sortOrder: sort.orderBy === 'cumu_return_trade' ? (sort.asc ? 'ascend' : 'descend') : null,
    },
    {
      key: 'returnTradeRatio',
      dataIndex: 'returnTradeRatio',
      title: t('退货/退款率'),
      width: 150,
      render: ratioRender,
    },
    {
      key: 'cumuComment',
      dataIndex: 'cumuComment',
      title: t('评价数'),
      width: 120,
    },
    {
      key: 'cumuBadComment',
      dataIndex: 'cumuBadComment',
      title: t('差评数'),
      width: 130,
      render: numRender,
      sorter: true,
      showSorterTooltip: false,
      sortOrder: sort.orderBy === 'cumu_bad_comment' ? (sort.asc ? 'ascend' : 'descend') : null,
    },
    {
      key: 'badCommentRatio',
      dataIndex: 'badCommentRatio',
      title: t('差评率'),
      width: 130,
      render: ratioRender,
    },
    {
      key: 'createTime',
      dataIndex: 'createTime',
      title: t('outofstock_table_marktime'),
      width: 180,
      render: (time: RT['createTime']) => dayjs(Number(time)).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('操作'),
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        const {
          tunnelType,
          region,
          tradeBuyerId,
          buyerNick,
          badComments,
          returnTrades,
          completeTrades,
        } = record;
        const mark = (isMalicious: boolean) => {
          return markHighRiskMalicious({
            tunnelType,
            region,
            tradeBuyerId,
            isMalicious,
          })
            .then(() => {
              message.success(t('phrase.保存成功'));
              highRiskState.refresh();
              maliciousState.refresh();
            })
            .catch(() => {
              message.error(t('phrase.保存失败，请稍后再试'));
            });
        };
        const actions =
          activeTabKey === 'malicious'
            ? [
                <StatusButton
                  danger
                  popConfirmProps={{
                    title: t('您确定要移出恶意买家吗？'),
                  }}
                  onClick={() => mark(false)}
                >
                  {t('移出恶意买家')}
                </StatusButton>,
              ]
            : [
                <StatusButton
                  className="!mr-4"
                  danger
                  popConfirmProps={{
                    title: t('您确定要标为恶意买家吗？'),
                  }}
                  onClick={() => mark(true)}
                >
                  {t('标为恶意买家')}
                </StatusButton>,
                <StatusButton type="primary" onClick={() => mark(false)}>
                  {t('标为非恶意买家')}
                </StatusButton>,
              ];
        const trades = [...badComments, ...returnTrades, ...completeTrades];
        return (
          <div>
            {trades.length > 0 && (
              <StatusButton
                type="link"
                onClick={() => {
                  return searchType({
                    type: 'trade_buyer_id',
                    keyword: tradeBuyerId,
                    tunnelType,
                    region,
                    pageSize: 99,
                  })
                    .then(({ searchRow }) => {
                      const noConversationRecords = trades.reduce<NoConversationRecord[]>(
                        (res, cur) => {
                          const { tradeId, externalStoreId, internalStoreId, storeName, alias } =
                            cur;
                          const target = res.find((_) => _.internalStoreId === internalStoreId);
                          if (target) {
                            target.externalTradeIds.push(tradeId);
                          } else {
                            res.push({
                              hasConversation: false,
                              buyerNick,
                              storeName,
                              storeAlias: alias,
                              externalBuyerId: tradeBuyerId,
                              externalStoreId,
                              internalStoreId,
                              externalTradeIds: [tradeId],
                              platform: tunnelType,
                              region,
                            });
                          }
                          return res;
                        },
                        [],
                      );
                      if (!searchRow.length) {
                        conversationsModalState.show({
                          buyer: record,
                          records: noConversationRecords,
                          actions,
                        });
                      } else {
                        const hasConversationRecords = searchRow.map<HasConversationRecord>(
                          ({
                            conversationId,
                            buyerNick,
                            platform,
                            region,
                            storeName,
                            storeAlias,
                            storeId,
                          }) => ({
                            hasConversation: true,
                            conversationId,
                            buyerNick,
                            platform,
                            region,
                            storeName,
                            storeAlias,
                            internalStoreId: storeId,
                          }),
                        );
                        conversationsModalState.show({
                          buyer: record,
                          records: noConversationRecords.reduce<ConversationRecord[]>(
                            (res, record) => {
                              if (
                                res.every(
                                  ({ internalStoreId }) =>
                                    internalStoreId !== record.internalStoreId,
                                )
                              ) {
                                res.push(record);
                              }
                              return res;
                            },
                            hasConversationRecords,
                          ),
                          actions,
                        });
                      }
                    })
                    .catch(() => {
                      message.error(t('phrase.获取数据失败，请稍后再试'));
                    });
                }}
              >
                {t('feedback.查看详情')}
              </StatusButton>
            )}
          </div>
        );
      },
    },
  ];

  const onFilterChange = (filter: BuyerFilterParams, sort?: BuyerSortParams) =>
    state.runAsync({
      page: {
        pageNum: 1,
        pageSize: page.pageSize,
      },
      sort,
      filter,
    });

  const [malicioused, setMalicioused] = useState(false);

  const filter = visible && [
    <BuyerFilter
      visible={activeTabKey === 'highRisk'}
      filter={highRiskFilter}
      onSearch={onFilterChange}
      onReset={() => onFilterChange(defaultFilter, defaultSort)}
    />,
    malicioused && (
      <BuyerFilter
        visible={activeTabKey === 'malicious'}
        filter={maliciousFilter}
        onSearch={onFilterChange}
        onReset={() => onFilterChange(defaultFilter, defaultSort)}
      />
    ),
  ];

  const exportHighRiskBuyerModalState = useModal();
  const exportMaliciousBuyerModalState = useModal();
  const exportMaliciousTradeModalState = useModal();
  const importMaliciousBuyerModalState = useModal();

  const exportHighRiskBuyerModal = (
    <ExportBuyerModal
      type="highRisk"
      visible={exportHighRiskBuyerModalState.visible}
      onCancel={exportHighRiskBuyerModalState.hide}
    />
  );
  const exportMaliciousBuyerModal = (
    <ExportBuyerModal
      type="malicious"
      visible={exportMaliciousBuyerModalState.visible}
      onCancel={exportMaliciousBuyerModalState.hide}
    />
  );
  const exportMaliciousTradeModal = (
    <ExportTradeModal
      visible={exportMaliciousTradeModalState.visible}
      onCancel={exportMaliciousTradeModalState.hide}
    />
  );
  const importMaliciousBuyerModal = (
    <ImportMaliciousBuyerModal
      visible={importMaliciousBuyerModalState.visible}
      onCancel={importMaliciousBuyerModalState.hide}
      onImport={state.run}
    />
  );

  return (
    <Container style={visible ? undefined : { display: 'none' }}>
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          setActiveTabKey(key as any);
          key === 'malicious' && setMalicioused(true);
          (key === 'highRisk' ? highRiskState : maliciousState).refresh();
        }}
        tabBarExtraContent={
          activeTabKey === 'highRisk' ? (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={exportHighRiskBuyerModalState.show}
            >
              {t('导出买家明细')}
            </Button>
          ) : (
            <div className="flex items-center">
              <Button
                type="link"
                icon={<UploadOutlined />}
                onClick={importMaliciousBuyerModalState.show}
              >
                {t('导入恶意买家')}
              </Button>
              <Divider type="vertical" />
              <Dropdown
                overlay={
                  <Menu
                    onClick={({ key }) => {
                      if (key === 'trade') {
                        exportMaliciousTradeModalState.show();
                      }
                      if (key === 'buyer') {
                        exportMaliciousBuyerModalState.show();
                      }
                    }}
                  >
                    <Menu.Item key="trade">{t('导出订单明细')}</Menu.Item>
                    <Menu.Item key="buyer">{t('导出买家明细')}</Menu.Item>
                  </Menu>
                }
              >
                <Button type="link" icon={<DownOutlined />}>
                  {t('导出买家明细')}
                </Button>
              </Dropdown>
            </div>
          )
        }
      >
        <Tabs.TabPane
          key="highRisk"
          tab={
            <div>
              {highRiskState.data ? `${t('高风险买家')}(${highRiskPage.total})` : t('高风险买家')}
              <Tooltip title={t('highRiskBuyerTips')}>
                <What />
              </Tooltip>
            </div>
          }
        />
        <Tabs.TabPane
          key="malicious"
          tab={maliciousState.data ? `${t('恶意买家')}(${maliciousPage.total})` : t('恶意买家')}
        />
      </Tabs>
      <section>
        <span>{filter}</span>
        <ScrollTable
          loading={state.loading}
          dataSource={state.data}
          columns={columns}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            current: page.pageNum,
            pageSize: page.pageSize,
            total: page.total,
            showTotal: (x) => t('feedback.共x项', { x }),
            onChange: (pageNum, pageSize) => {
              state.run({
                page: {
                  pageNum,
                  pageSize,
                },
              });
            },
          }}
          onChange={(_, __, { field }: any, { action }) => {
            if (action === 'sort') {
              const orderBy = (
                {
                  cumuReturnTrade: 'cumu_return_trade',
                  cumuBadComment: 'cumu_bad_comment',
                } as any
              )[field];
              const asc = orderBy === sort.orderBy ? !sort.asc : false;
              state.run({
                sort: {
                  orderBy,
                  asc,
                },
              });
            }
          }}
        />
      </section>
      {conversationsModal}
      {exportHighRiskBuyerModal}
      {exportMaliciousBuyerModal}
      {exportMaliciousTradeModal}
      {importMaliciousBuyerModal}
    </Container>
  );
}

const Container = styled.section`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 16px;
  > .ant-tabs {
    flex: 0 0 auto;
    background: #fff;
    .ant-tabs-nav {
      margin-bottom: 0;
      padding: 0 24px;
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    padding: 24px;
    background: #fff;
    > span {
      margin-bottom: 16px;
    }
    > div {
      flex: 1 0 0;
    }
  }
`;

const Red = styled.div`
  color: var(--ant-error-color);
`;

export default Buyer;
